/**
 * useStealthContacts - what the party knows of the figures it cannot see
 * (plan map-display, H; engine/stealth/contacts).
 *
 * The GM sets a hidden figure's level (unknown / signs / contact) from its
 * menu; the levels live on the GM's desk (`stealth-contacts`), never on a
 * player's machine. The GM's screen turns them into what players may know -
 * opaque ids, last seen, trails, bearings from the party - and publishes
 * that on the Stealth frame, writing only a change. A figure revealed or
 * gone drops out: only the hidden are contacts. A REVEALED figure's record is
 * forgotten (`forget`, called by the reveal itself), so hidden again it starts
 * unknown (2026-10-21). Never by watching the hidden set shrink: a set that
 * empties for a moment (a resubscription, a seat change) would wipe the desk.
 *
 * The director's Contacts list (2026-10-21, "the GM, made easy") reads
 * `list`: every hidden figure by name, its level, and its bearing from the
 * party. Empty on any other screen - the names are the GM's.
 *
 * @param {object} s - the shell scope: stealth (useShellStealth), hidden (useHiddenFigures), tokens, roster,
 *   direction, actor, sessionId, isGm.
 * @returns {{ contacts: {
 *   levelOf: (id: string) => string, setLevel: (id: string, level: string) => void,
 *   list: { id: string, name: string, level: string, compass: string | null }[],
 *   allUnknownToSigns: () => void, allToUnknown: () => void, forget: (id: string) => void } }}
 */
import { useCallback, useEffect, useMemo } from 'react';
import * as multiplayer from '../multiplayer';
import useGmDeskValue from './useGmDeskValue';
import {
  compassFrom,
  forgetFigures,
  publicContacts,
  readContactDesk,
  sameContacts,
  setContactLevel,
  withLevels,
} from '../engine/stealth/contacts';
import { placedParty } from '../engine/stealth/opposition';

const DESK_ID = 'stealth-contacts';
const NONE = new Set();

/** The desk with only the figures still hidden: a revealed or ended one is no contact. */
function onlyHidden(desk, hiddenIds) {
  const figures = Object.fromEntries(Object.entries(desk.figures).filter(([id]) => hiddenIds.has(id)));
  return { ...desk, figures };
}

/** The ground points of the party's tokens (`placedParty`, the sightings' party too). */
const partyPoints = (roster, opposition, tokens) =>
  placedParty(roster, opposition, tokens).map(({ at }) => ({ x: at.x, z: at.z }));

/**
 * Write what players may know onto the Stealth frame - and again whenever the frame's copy differs: an Undo or a
 * scene cut puts back a frame without it, and the desk has not changed to say so.
 */
function usePublished(published, stealth) {
  const { apply } = stealth;
  const onFrame = stealth.state.contacts;
  useEffect(() => {
    if (!published || sameContacts(onFrame, published)) return;
    apply((st) => (sameContacts(st.contacts, published) ? st : { ...st, contacts: published }));
  }, [published, onFrame, apply]);
}

/** The director's list: each hidden figure by name, its level, its bearing from the party. */
function listOf(hiddenIds, { hidden, roster, desk, party }) {
  return [...hiddenIds].map((id) => {
    const token = hidden?.hiddenTokens?.[id];
    const name = hidden?.figures?.get(id)?.combatant?.name ?? roster?.find((c) => c.id === id)?.name ?? id;
    return { id, name, level: desk.figures[id]?.level ?? 'unknown', compass: compassFrom(party, token) };
  });
}

/** The director's verbs: one figure's level, and the list's two buttons. */
function useLevelVerbs(desk, setRaw, hiddenIds, hiddenTokens) {
  const setLevel = useCallback(
    (id, level) => {
      const token = hiddenTokens?.[id];
      setRaw(setContactLevel(desk, id, level, token ? { x: token.x, z: token.z } : undefined));
    },
    [desk, hiddenTokens, setRaw],
  );
  // Each button moves only the figures it changes, and writes nothing when there are none.
  const moveAll = useCallback(
    (from, to) => {
      const ids = [...hiddenIds].filter((id) => from(desk.figures[id]?.level ?? 'unknown'));
      if (ids.length) setRaw(withLevels(desk, ids, to));
    },
    [desk, hiddenIds, setRaw],
  );
  const allUnknownToSigns = useCallback(() => moveAll((l) => l === 'unknown', 'signs'), [moveAll]);
  const allToUnknown = useCallback(() => moveAll((l) => l !== 'unknown', 'unknown'), [moveAll]);
  // Revealed (hooks/useHideReveal): the record goes, so hidden again the figure starts unknown. No record, no write.
  const forget = useCallback(
    (id) => {
      const next = forgetFigures(desk, [id]);
      if (next !== desk) setRaw(next);
    },
    [desk, setRaw],
  );
  return { setLevel, allUnknownToSigns, allToUnknown, forget };
}

export default function useStealthContacts(s) {
  const seat = useMemo(() => ({ sessionId: s.sessionId, isGm: !!s.isGm, mp: multiplayer }), [s.sessionId, s.isGm]);
  const [raw, setRaw] = useGmDeskValue(seat, DESK_ID);
  const desk = useMemo(() => readContactDesk(raw), [raw]);
  const { stealth, hidden, tokens, roster } = s;
  const hiddenIds = hidden?.hiddenIds ?? NONE;
  const hiddenTokens = hidden?.hiddenTokens;
  const party = useMemo(() => partyPoints(roster, stealth.opposition, tokens), [roster, stealth.opposition, tokens]);
  // Only in Stealth, where something shows it: elsewhere it would be frame writes nobody reads.
  const inStealth = s.direction?.frame?.phase === 'stealth';
  const published = useMemo(
    () =>
      stealth.canDirect && inStealth ? publicContacts(onlyHidden(desk, hiddenIds), hiddenTokens ?? {}, party) : null,
    [stealth.canDirect, inStealth, desk, hiddenIds, hiddenTokens, party],
  );
  usePublished(published, stealth);
  const verbs = useLevelVerbs(desk, setRaw, hiddenIds, hiddenTokens);
  const list = useMemo(
    () => (stealth.canDirect ? listOf(hiddenIds, { hidden, roster, desk, party }) : []),
    [stealth.canDirect, hiddenIds, hidden, roster, desk, party],
  );
  return { contacts: { levelOf: (id) => desk.figures[id]?.level ?? 'unknown', list, ...verbs } };
}
